import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { DataManagerService } from '../../../../../data/services/DataManager/data-manager.service';
import { AppConstants } from '../../../../../shared/constants/app-constants';

/** Rows per group; the panel shows a handful, not a page. */
export const RECORDS_PER_GROUP = 5;
/** Default per-request timeout; a provider backed by a slower store can pass its own. */
export const DEFAULT_REQUEST_TIMEOUT_MS = 8000;

/** src=R makes the list endpoints search Redshift (per the backend team). */
export const REDSHIFT_PARAMS: Record<string, string> = { src: 'R' };
/** Redshift answers in seconds, not milliseconds; the 8s default cut /charges off on .io. */
export const REDSHIFT_TIMEOUT_MS = 20000;

/**
 * GET <service>?limit=&page=1&search=<query>[&extra…] — the same raw `search` param the list screens
 * send (BaseListComponent / DisputesComponent), so the backend's own multi-field search applies.
 * Resolves to the response's `data` rows; any error or timeout resolves to [] so one failing
 * group never blanks the others.
 */
export function listSearch(dataManager: DataManagerService, serviceName: string, query: string,
                           extraParams: Record<string, string> = {},
                           timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS): Observable<any[]> {
  // Keys are developer-defined and may carry the backend's bracket filters (report_date[gte]); values are encoded.
  const extra = Object.keys(extraParams)
    .map(key => '&' + key + '=' + encodeURIComponent(extraParams[key]))
    .join('');
  const url = AppConstants.BASE_URL + serviceName
    + '?limit=' + RECORDS_PER_GROUP + '&page=1&search=' + encodeURIComponent(query) + extra;
  return dataManager.getRequest(url, 0).pipe(
    timeout(timeoutMs),
    map(res => (res && Array.isArray(res.data) ? res.data : [])),
    catchError(() => of([]))
  );
}

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** Formats a dollar amount; '' when not a number. */
export function formatUsd(dollars: any): string {
  const n = Number(dollars);
  return dollars === null || dollars === undefined || dollars === '' || isNaN(n) ? '' : USD.format(n);
}

/** Local date as yyyy/MM/dd — the format the list screens send in date filters. */
export function formatFilterDate(date: Date): string {
  const pad = (n: number) => (n < 10 ? '0' : '') + n;
  return date.getFullYear() + '/' + pad(date.getMonth() + 1) + '/' + pad(date.getDate());
}

/** Joins the non-empty parts with ' · '. */
export function joinParts(...parts: any[]): string {
  return parts.filter(p => p !== null && p !== undefined && String(p).trim() !== '').join(' · ');
}
